export type ZeroLogLevel = "error" | "warn" | "info" | "debug"

export interface ZeroLogEntry {
	at: number
	level: ZeroLogLevel
	message: string
}

export interface ZeroLogBuffer {
	push: (level: ZeroLogLevel, args: readonly unknown[], at?: number) => void
	entries: () => ZeroLogEntry[]
	clear: () => void
}

export const ZERO_LOG_BUFFER_SIZE = 60
export const ZERO_LOG_ENTRY_MAX_CHARS = 600

// These lines carry synced rows, query ASTs or mutation payloads, none of which
// may reach an error report.
const DROPPED_MESSAGE_PREFIXES = [
	"Slow query materialization",
	"Received an error response when pushing mutations",
	'Mutator "',
	"clearing due to unexpected poke error",
]

const SENT_MESSAGE_DUMP = "Recent sent messages:"

const SAFE_CONTEXT_KEYS = new Set([
	"attempt",
	"baseCookie",
	"code",
	"connectErrorCount",
	"connectStart",
	"connectedAt",
	"connectedCount",
	"connectMsgLatencyMs",
	"connectionDuration",
	"connectionState",
	"disconnectAt",
	"kind",
	"lmid",
	"messageCount",
	"navigatorOnline",
	"proceedingConnectErrorCount",
	"reason",
	"timeToConnectMs",
	"timeToOpenMs",
	"totalTimeToConnectMs",
	"totalToConnectStart",
	"wasClean",
	"wsid",
])

// A 1009 close embeds up to five snippets of the JSON the client last sent,
// which can include mutation args.
export function scrubZeroMessage(message: string): string {
	const at = message.indexOf(SENT_MESSAGE_DUMP)
	return at === -1 ? message : `${message.slice(0, at)}[redacted]`
}

function safeScalar(value: unknown): string | number | boolean | null {
	if (value === null) return null
	if (typeof value === "boolean" || typeof value === "number") return value
	if (typeof value === "string") return scrubZeroMessage(value).slice(0, 200)
	if (value instanceof Error) return `${value.name}: ${value.message}`
	return typeof value
}

function redactObject(value: object): string {
	const kept: Record<string, string | number | boolean | null> = {}
	for (const [key, raw] of Object.entries(value)) {
		if (!SAFE_CONTEXT_KEYS.has(key)) continue
		kept[key] = safeScalar(raw)
	}
	try {
		return JSON.stringify(kept) ?? "{}"
	} catch {
		return "{}"
	}
}

function formatOne(arg: unknown): string {
	if (typeof arg === "string") return scrubZeroMessage(arg)
	if (arg instanceof Error) {
		const kind = (arg as { kind?: unknown }).kind
		const label = typeof kind === "string" ? `${arg.name}(${kind})` : arg.name
		return `${label}: ${scrubZeroMessage(arg.message)}`
	}
	if (arg === null) return "null"
	if (Array.isArray(arg)) return `[array:${arg.length}]`
	if (typeof arg !== "object") return String(arg)
	return redactObject(arg)
}

export function isDroppedZeroLogLine(args: readonly unknown[]): boolean {
	const first = args[0]
	if (typeof first !== "string") return false
	return DROPPED_MESSAGE_PREFIXES.some((prefix) => first.startsWith(prefix))
}

export function formatZeroLogArgs(args: readonly unknown[]): string {
	return args.map(formatOne).join(" ").slice(0, ZERO_LOG_ENTRY_MAX_CHARS)
}

export function createZeroLogBuffer(
	size: number = ZERO_LOG_BUFFER_SIZE,
): ZeroLogBuffer {
	let entries: ZeroLogEntry[] = []
	return {
		push: (level, args, at = Date.now()) => {
			if (size <= 0) return
			if (isDroppedZeroLogLine(args)) return
			entries.push({ at, level, message: formatZeroLogArgs(args) })
			if (entries.length > size) entries = entries.slice(entries.length - size)
		},
		entries: () => [...entries],
		clear: () => {
			entries = []
		},
	}
}
